/**
 * User-Facing Residuals Dashboard (Enhanced)
 * Residuals, Statistics with Trends, Bonuses.
 * Mobile responsive. Historical trend tracking.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Store, DollarSign, CheckCircle, Clock, Download,
  FileText, AlertTriangle, Eye, BarChart3, TrendingUp,
  ArrowUpRight, ArrowDownRight, Activity, Award,
  FileSpreadsheet, Minus, Plus, ArrowDown
} from 'lucide-react';

import { displayProcessor, uniqueProcessors } from '../lib/processorDisplay';
import { PayoutStatementsCard } from '../components/finance/PayoutStatementsCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROCESSORS = ['All Processors', ...uniqueProcessors(['TSYS-msc', 'North', 'FiservCopilot', 'TSYS', 'Elavon', 'EPX', 'First Data'])];

const RANGE_OPTIONS = [
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'semester', label: 'This Semester' },
  { value: 'year', label: 'This Year' },
  { value: '2year', label: 'Last 2 Years' },
  { value: 'all', label: 'All Time (Since Day 1)' },
];

export default function UserResidualsView() {
  const { user } = useAuthStore();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('residuals');
  const [processor, setProcessor] = useState('all');
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);
  const [showMerchantDetail, setShowMerchantDetail] = useState(false);
  const [notification, setNotification] = useState(true);
  const [statementModal, setStatementModal] = useState(null);
  const [statementRange, setStatementRange] = useState('all');
  const [statement, setStatement] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (processor !== 'all') params.append('processor', processor);
      const [resData, trendData] = await Promise.all([
        api.request(`/residuals/my-residuals?${params}`),
        api.request('/residuals/trends?months=12'),
      ]);
      setData(resData);
      setTrends(trendData);
    } catch (err) {
      console.error('Failed to load residuals:', err);
    }
    setLoading(false);
  }, [processor]);

  useEffect(() => { loadData(); }, [loadData]);

  const requestPayout = async () => {
    try {
      await api.request('/residuals/request-payout', { method: 'POST' });
      toast.success('Payout request submitted to your upline');
    } catch (err) {
      toast.error(err.message || 'Failed to submit payout request');
    }
  };

  const loadStatement = useCallback(async (range) => {
    setStmtLoading(true);
    try {
      const res = await api.request(`/residuals/earnings-statement?range_type=${range}`);
      setStatement(res);
    } catch (err) {
      console.error('Failed to load statement:', err);
    }
    setStmtLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'statements') loadStatement(statementRange);
  }, [activeTab, statementRange, loadStatement]);

  const downloadStatement = async (format) => {
    setDownloading(format);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/residuals/earnings-statement/download?format=${format}&range_type=${statementRange}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings_statement_${statementRange}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Download failed');
    }
    setDownloading(null);
  };

  const stats = data?.stats || {};
  const reports = data?.reports || [];
  const bonuses = data?.bonuses || [];
  const totalBonuses = data?.total_bonuses || 0;
  const trendData = trends?.trends || [];
  const trendSummary = trends?.summary || {};

  const filteredReports = showUnpaidOnly
    ? reports.filter(r => r.payout_status !== 'paid')
    : reports;

  // Group reports by processor
  const byProcessor = {};
  filteredReports.forEach(r => {
    const proc = r.processor || 'Unknown';
    if (!byProcessor[proc]) {
      byProcessor[proc] = { processor: proc, grossProfit: 0, cost: 0, netProfit: 0, payout: 0, merchants: 0 };
    }
    byProcessor[proc].grossProfit += r.gross_revenue || 0;
    byProcessor[proc].cost += r.processing_costs || 0;
    byProcessor[proc].netProfit += r.net_revenue || 0;
    byProcessor[proc].payout += r.user_payout || 0;
    byProcessor[proc].merchants += 1;
  });

  const tabContent = [
    { key: 'residuals', label: 'Residuals' },
    { key: 'statements', label: 'Statements' },
    { key: 'statistics', label: 'Statistics' },
    { key: 'bonuses', label: 'Bonuses' },
  ];

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="user-residuals-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold font-heading" data-testid="residuals-page-title">Residuals</h1>
            <span className="text-[10px] sm:text-xs px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full font-medium capitalize">
              {user?.role?.replace(/_/g, ' ')} View
            </span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Your split: <strong>{data?.split_percentage || 0}%</strong> of upline's revenue</p>
        </div>
      </div>

      <PayoutStatementsCard />

      {/* Notification Banner */}
      {notification && stats.unpaid > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 sm:p-4 flex items-center gap-3" data-testid="payout-notification">
          <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
          <p className="text-xs sm:text-sm text-orange-800 flex-1">
            <strong>Pending Payouts:</strong> You have ${stats.unpaid?.toFixed(2)} in unpaid residuals.
          </p>
          <Button variant="outline" size="sm" className="text-orange-700 border-orange-200 bg-orange-50 hover:bg-orange-100 text-xs flex-shrink-0" onClick={requestPayout}>
            Request Payout
          </Button>
          <button onClick={() => setNotification(false)} className="text-orange-400 hover:text-orange-600 text-lg">&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b overflow-x-auto">
        {tabContent.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 sm:px-5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === t.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* === RESIDUALS TAB === */}
      {activeTab === 'residuals' && (
        <div className="space-y-5">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={processor} onValueChange={setProcessor}>
              <SelectTrigger className="w-40 sm:w-48" data-testid="filter-processor">
                <SelectValue placeholder="All Processors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Processors</SelectItem>
                {PROCESSORS.slice(1).map(p => <SelectItem key={p} value={p}>{displayProcessor(p)}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={showUnpaidOnly} onChange={e => setShowUnpaidOnly(e.target.checked)} className="accent-primary" />
              <span className="text-xs sm:text-sm">Unpaid only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={showMerchantDetail} onChange={e => setShowMerchantDetail(e.target.checked)} className="accent-primary" />
              <span className="text-xs sm:text-sm">Detail</span>
            </label>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { label: 'Active Merchants', value: stats.active_merchants || 0, icon: Store, color: 'text-blue-600 bg-blue-50' },
              { label: 'Total Payout Due', value: `$${(stats.total_payout_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, icon: DollarSign, color: 'text-green-600 bg-green-50' },
              { label: 'Paid', value: `$${(stats.paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, icon: CheckCircle, color: 'text-green-600 bg-green-50' },
              { label: 'Unpaid', value: `$${(stats.unpaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`, icon: Clock, color: 'text-orange-600 bg-orange-50' },
            ].map(s => (
              <div key={s.label} className="bg-card border rounded-xl p-3 sm:p-5">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center ${s.color}`}>
                    <s.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground font-medium">{s.label}</p>
                    <p className="text-base sm:text-xl font-bold" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>{s.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Live Processor Commissions (Maverick sync) — kept separate from manual reports below */}
          {data?.processor_commissions?.entries?.length > 0 && (
            <div className="bg-card border rounded-xl overflow-hidden" data-testid="processor-commissions-section">
              <div className="flex items-center justify-between p-3 sm:p-4 border-b">
                <h3 className="font-semibold text-sm">Live Processor Commissions</h3>
                <span className={`text-base sm:text-lg font-bold ${data.processor_commissions.total < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="processor-commissions-total">
                  ${data.processor_commissions.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground px-3 sm:px-4 py-2">
                Synced directly from the processor and calculated against your Schedule A split — separate from the manual entries below.
              </p>
              <div className="overflow-x-auto max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40 sticky top-0"><tr>
                    <th className="text-left px-3 sm:px-4 py-2">Period</th><th className="text-left px-3 sm:px-4 py-2">MID</th>
                    <th className="text-right px-3 sm:px-4 py-2">Split %</th><th className="text-right px-3 sm:px-4 py-2">Entitled</th>
                  </tr></thead>
                  <tbody>
                    {data.processor_commissions.entries.slice(0, 50).map(e => (
                      <tr key={e.id} className="border-t" data-testid={`processor-commission-row-${e.id}`}>
                        <td className="px-3 sm:px-4 py-2">{e.period}</td>
                        <td className="px-3 sm:px-4 py-2">{e.mid}</td>
                        <td className="px-3 sm:px-4 py-2 text-right">{e.split_percentage}%</td>
                        <td className={`px-3 sm:px-4 py-2 text-right font-semibold ${e.entitled_amount < 0 ? 'text-red-600' : ''}`}>${e.entitled_amount?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border-b gap-2">
              <h3 className="font-semibold text-sm">Residual Summary</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid="export-csv-btn">
                  <Download className="h-3.5 w-3.5 mr-1" /> CSV
                </Button>
                <Button size="sm" onClick={() => setStatementModal({ period: 'current' })} data-testid="view-statement-btn">
                  <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="residuals-summary-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Platform</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Merchants</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gross</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Cost</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Net</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Split %</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payout</th>
                    <th className="text-right py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">Loading...</td></tr>
                  ) : Object.keys(byProcessor).length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-12 text-muted-foreground text-sm">
                      No residual data available yet.
                    </td></tr>
                  ) : Object.values(byProcessor).map(group => (
                    <tr key={group.processor} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="py-3 px-3 sm:px-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs sm:text-sm font-medium">{displayProcessor(group.processor)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm hidden sm:table-cell">{group.merchants}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm">${group.grossProfit.toFixed(2)}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm hidden md:table-cell">${group.cost.toFixed(2)}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm font-medium">${group.netProfit.toFixed(2)}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm hidden lg:table-cell">{data?.split_percentage || 0}%</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm font-bold">${group.payout.toFixed(2)}</td>
                      <td className="py-3 px-3 sm:px-4 text-right">
                        <Button variant="outline" size="sm" className="text-xs text-orange-700 border-orange-200 bg-orange-50 hover:bg-orange-100" onClick={requestPayout} data-testid="request-payout-btn">
                          Payout
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Merchant Detail */}
          {showMerchantDetail && (
            <div className="bg-card border rounded-xl overflow-hidden">
              <div className="p-3 sm:p-4 border-b">
                <h3 className="font-semibold text-sm">Merchant-Level Detail</h3>
              </div>
              <div className="p-3 sm:p-4 space-y-2">
                {filteredReports.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">No merchant-level data available</p>
                ) : filteredReports.map((r, idx) => (
                  <div key={r.id || idx} className="bg-muted/30 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h4 className="font-medium text-sm">{r.merchant_name || `Merchant #${idx + 1}`}</h4>
                      <p className="text-xs text-muted-foreground">MID: {r.merchant_id || 'N/A'} — {displayProcessor(r.processor) || 'Unknown'}</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-sm font-bold text-green-700">Your Share: ${r.user_payout?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">From ${r.net_revenue?.toFixed(2)} net</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === STATEMENTS TAB === */}
      {activeTab === 'statements' && (
        <div className="space-y-5">
          {/* Range Selector + Download Buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <Select value={statementRange} onValueChange={setStatementRange}>
              <SelectTrigger className="w-52" data-testid="statement-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadStatement('xlsx')}
                disabled={downloading === 'xlsx' || stmtLoading}
                data-testid="download-xlsx-btn"
              >
                {downloading === 'xlsx' ? (
                  <span className="animate-spin mr-2">&#9696;</span>
                ) : (
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
                )}
                Excel
              </Button>
              <Button
                size="sm"
                onClick={() => downloadStatement('pdf')}
                disabled={downloading === 'pdf' || stmtLoading}
                data-testid="download-pdf-stmt-btn"
              >
                {downloading === 'pdf' ? (
                  <span className="animate-spin mr-2">&#9696;</span>
                ) : (
                  <FileText className="h-3.5 w-3.5 mr-1.5" />
                )}
                PDF
              </Button>
            </div>
          </div>

          {stmtLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          ) : statement ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-card border rounded-xl p-4">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Residual Payouts</p>
                  <p className="text-lg sm:text-xl font-bold text-green-700 mt-1" data-testid="stmt-residual-payout">
                    ${statement.income.residual_payout.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </p>
                  <p className="text-[10px] text-muted-foreground">Schedule A x {statement.split_percentage}%</p>
                </div>
                <div className="bg-card border rounded-xl p-4">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Bonuses</p>
                  <p className="text-lg sm:text-xl font-bold text-blue-700 mt-1" data-testid="stmt-bonuses">
                    ${statement.income.bonus_income.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </p>
                </div>
                <div className="bg-card border rounded-xl p-4">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Deductions</p>
                  <p className="text-lg sm:text-xl font-bold text-red-600 mt-1" data-testid="stmt-deductions">
                    -${statement.deductions.total_deductions.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </p>
                  <p className="text-[10px] text-muted-foreground">ACH / Install / Clawbacks</p>
                </div>
                <div className="bg-card border rounded-xl p-4 border-primary/30">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Net Earnings</p>
                  <p className={`text-lg sm:text-xl font-bold mt-1 ${statement.summary.net_earnings >= 0 ? 'text-green-700' : 'text-red-600'}`}
                    data-testid="stmt-net-earnings"
                  >
                    ${statement.summary.net_earnings.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{RANGE_OPTIONS.find(r => r.value === statementRange)?.label}</p>
                </div>
              </div>

              {/* Earnings Breakdown Table */}
              <div className="bg-card border rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                  <h3 className="font-semibold text-sm">Earnings Breakdown</h3>
                </div>
                <div className="p-4 space-y-3">
                  {/* INCOME Section */}
                  <div className="bg-green-50/50 rounded-xl p-4 space-y-2">
                    <h4 className="text-xs font-bold text-green-800 uppercase tracking-wider flex items-center gap-2">
                      <Plus className="h-3.5 w-3.5" /> Income
                    </h4>
                    <div className="flex justify-between py-1.5 border-b border-green-200/50">
                      <span className="text-sm">Residual Payouts (Schedule A x {statement.split_percentage}%)</span>
                      <span className="text-sm font-bold text-green-700">${statement.income.residual_payout.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-green-200/50">
                      <span className="text-sm">Bonuses Received</span>
                      <span className="text-sm font-bold text-green-700">${statement.income.bonus_income.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between py-1.5 pt-2">
                      <span className="text-sm font-bold">Total Income</span>
                      <span className="text-sm font-bold text-green-700">${statement.income.total_income.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  {/* DEDUCTIONS Section */}
                  <div className="bg-red-50/50 rounded-xl p-4 space-y-2">
                    <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider flex items-center gap-2">
                      <Minus className="h-3.5 w-3.5" /> Deductions
                    </h4>
                    <div className="flex justify-between py-1.5 border-b border-red-200/50">
                      <span className="text-sm">Equipment ACH Charges</span>
                      <span className="text-sm font-medium text-red-600">-${statement.deductions.equipment_ach.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-red-200/50">
                      <span className="text-sm">Installation Costs</span>
                      <span className="text-sm font-medium text-red-600">-${statement.deductions.installation_costs.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-red-200/50">
                      <span className="text-sm">Bonus Clawbacks</span>
                      <span className="text-sm font-medium text-red-600">-${statement.deductions.bonus_clawbacks.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between py-1.5 pt-2">
                      <span className="text-sm font-bold">Total Deductions</span>
                      <span className="text-sm font-bold text-red-600">-${statement.deductions.total_deductions.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  {/* NET */}
                  <div className="bg-primary/5 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-base font-bold">Net Earnings</span>
                      <span className={`text-xl font-bold ${statement.summary.net_earnings >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        ${statement.summary.net_earnings.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Period Breakdown */}
              {statement.period_breakdown.length > 0 && (
                <div className="bg-card border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b bg-muted/30">
                    <h3 className="font-semibold text-sm">Period Breakdown</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="period-breakdown-table">
                      <thead>
                        <tr className="border-b bg-muted/20">
                          <th className="text-left py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Period</th>
                          <th className="text-left py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Merchants</th>
                          <th className="text-right py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Gross</th>
                          <th className="text-right py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Costs</th>
                          <th className="text-right py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Net</th>
                          <th className="text-right py-3 px-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Your Payout</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.period_breakdown.map(p => (
                          <tr key={p.period} className="border-b last:border-0 hover:bg-muted/20">
                            <td className="py-3 px-4 text-sm font-medium">{p.period}</td>
                            <td className="py-3 px-4 text-sm hidden sm:table-cell">{p.merchants}</td>
                            <td className="py-3 px-4 text-sm text-right">${p.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                            <td className="py-3 px-4 text-sm text-right hidden md:table-cell">${p.costs.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                            <td className="py-3 px-4 text-sm text-right">${p.net.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                            <td className="py-3 px-4 text-sm text-right font-bold text-green-700">${p.payout.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Deduction Line Items */}
              {statement.deductions.items.length > 0 && (
                <div className="bg-card border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b bg-muted/30">
                    <h3 className="font-semibold text-sm">Deduction Details</h3>
                  </div>
                  <div className="divide-y">
                    {statement.deductions.items.map((item, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between" data-testid={`deduction-item-${idx}`}>
                        <div>
                          <p className="text-sm font-medium">{item.description}</p>
                          <p className="text-xs text-muted-foreground">{item.date} — {item.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                        </div>
                        <span className="text-sm font-bold text-red-600">-${item.amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cumulative Lifetime */}
              <div className="bg-card border-2 border-primary/20 rounded-xl p-5 space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" /> Cumulative — Since Day 1
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-green-50/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">Lifetime Payouts</p>
                    <p className="text-base sm:text-lg font-bold text-green-700" data-testid="cumulative-payout">
                      ${statement.cumulative.total_payout.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="bg-blue-50/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">Lifetime Bonuses</p>
                    <p className="text-base sm:text-lg font-bold text-blue-700">
                      ${statement.cumulative.total_bonuses.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="bg-red-50/50 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground uppercase">Lifetime Deductions</p>
                    <p className="text-base sm:text-lg font-bold text-red-600">
                      -${statement.cumulative.total_deductions.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="bg-primary/5 rounded-lg p-3 border border-primary/20">
                    <p className="text-[10px] text-muted-foreground uppercase">Lifetime Net</p>
                    <p className={`text-base sm:text-lg font-bold ${statement.cumulative.net_earnings >= 0 ? 'text-green-700' : 'text-red-600'}`}
                      data-testid="cumulative-net"
                    >
                      ${statement.cumulative.net_earnings.toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-16 bg-card border rounded-xl">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-sm text-muted-foreground">No statement data available</p>
            </div>
          )}
        </div>
      )}

      {/* === STATISTICS TAB === */}
      {activeTab === 'statistics' && (
        <div className="space-y-5">
          {/* Summary Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-card border rounded-xl p-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Income</p>
              <p className="text-lg sm:text-xl font-bold text-green-700">${(stats.total_payout_due || 0).toFixed(2)}</p>
            </div>
            <div className="bg-card border rounded-xl p-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Paid</p>
              <p className="text-lg sm:text-xl font-bold">${(stats.paid || 0).toFixed(2)}</p>
            </div>
            <div className="bg-card border rounded-xl p-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Unpaid</p>
              <p className="text-lg sm:text-xl font-bold text-orange-600">${(stats.unpaid || 0).toFixed(2)}</p>
            </div>
            <div className="bg-card border rounded-xl p-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Bonuses</p>
              <p className="text-lg sm:text-xl font-bold text-blue-600">${totalBonuses.toFixed(2)}</p>
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="bg-card border rounded-xl p-4 sm:p-5">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <Activity className="h-4 w-4 text-primary" /> Revenue Trend
            </h3>
            {trendData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-10 w-10 mx-auto mb-2 opacity-20" />
                <p className="text-sm">No historical data yet</p>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-1.5 sm:gap-2 h-36 sm:h-48" data-testid="user-trend-chart">
                  {trendData.map((t) => {
                    const maxVal = Math.max(...trendData.map(x => x.gross_revenue), 1);
                    const height = (t.gross_revenue / maxVal) * 100;
                    return (
                      <div key={t.period} className="flex-1 flex flex-col items-center gap-1 group">
                        <div className="relative w-full flex justify-center">
                          <div className="absolute -top-6 opacity-0 group-hover:opacity-100 transition-opacity bg-foreground text-background text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                            ${t.gross_revenue.toLocaleString()}
                          </div>
                          <div
                            className="w-full max-w-[32px] bg-primary/80 rounded-t hover:bg-primary transition-colors cursor-pointer"
                            style={{ height: `${Math.max(height, 3)}%` }}
                          />
                        </div>
                        <span className="text-[8px] sm:text-[9px] text-muted-foreground truncate w-full text-center">{t.period}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Avg Monthly</p>
                    <p className="text-sm font-bold">${(trendSummary.avg_monthly_payout || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Peak Month</p>
                    <p className="text-sm font-bold">{trendSummary.peak_month || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Total Earned</p>
                    <p className="text-sm font-bold text-green-700">${(trendSummary.total_earned || 0).toFixed(2)}</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-card border rounded-xl p-4 sm:p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <BarChart3 className="h-4 w-4 text-primary" /> Portfolio
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Total Earned</p>
                  <p className="text-base sm:text-lg font-bold">${(stats.total_payout_due || 0).toFixed(2)}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Avg/Merchant</p>
                  <p className="text-base sm:text-lg font-bold">
                    ${stats.active_merchants ? ((stats.total_payout_due || 0) / stats.active_merchants).toFixed(2) : '0.00'}
                  </p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Active Merchants</p>
                  <p className="text-base sm:text-lg font-bold">{stats.active_merchants || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Your Split</p>
                  <p className="text-base sm:text-lg font-bold">{data?.split_percentage || 0}%</p>
                </div>
              </div>
            </div>
            <div className="bg-card border rounded-xl p-4 sm:p-5 space-y-3">
              <h3 className="font-semibold flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4 text-primary" /> Accounts
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Submitted</p>
                  <p className="text-base sm:text-lg font-bold">{stats.active_merchants || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Processing</p>
                  <p className="text-base sm:text-lg font-bold">{stats.active_merchants || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Total Bonuses</p>
                  <p className="text-base sm:text-lg font-bold text-green-700">${totalBonuses.toFixed(2)}</p>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Bonus Count</p>
                  <p className="text-base sm:text-lg font-bold">{bonuses.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Splits */}
          {data?.risk_splits && Object.keys(data.risk_splits).length > 0 && (
            <div className="bg-card border rounded-xl p-4 sm:p-5 space-y-3">
              <h3 className="font-semibold text-sm">Schedule A — Risk-Based Splits</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(data.risk_splits).map(([key, val]) => (
                  <div key={key} className="bg-muted/30 rounded-lg p-3">
                    <p className="text-[10px] text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-bold">{val.percentage}%</p>
                    {val.true_split && <span className="text-[10px] text-blue-600 font-medium">True Split</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* === BONUSES TAB === */}
      {activeTab === 'bonuses' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-card border rounded-xl p-4 sm:p-5">
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Total Received</p>
              <p className="text-xl sm:text-2xl font-bold text-green-700 mt-1" data-testid="user-total-bonuses">
                ${totalBonuses.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-card border rounded-xl p-4 sm:p-5">
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Bonus Count</p>
              <p className="text-xl sm:text-2xl font-bold mt-1">{bonuses.length}</p>
            </div>
            <div className="bg-card border rounded-xl p-4 sm:p-5">
              <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider">Last Bonus</p>
              <p className="text-xl sm:text-2xl font-bold mt-1">{bonuses.length > 0 ? new Date(bonuses[0].created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>

          <div className="bg-card border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="user-bonuses-table">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Description</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Period</th>
                    <th className="text-left py-3 px-3 sm:px-4 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bonuses.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-12 text-muted-foreground text-sm">No bonuses received yet</td></tr>
                  ) : bonuses.map((b, idx) => (
                    <tr key={b.id || idx} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm">{new Date(b.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm font-bold text-green-700">${b.amount?.toFixed(2)}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm hidden sm:table-cell">{b.description || '-'}</td>
                      <td className="py-3 px-3 sm:px-4 text-xs sm:text-sm hidden md:table-cell">{b.period || '-'}</td>
                      <td className="py-3 px-3 sm:px-4"><span className="text-[10px] sm:text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 font-medium">{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Statement Modal removed — now a full tab */}
    </div>
  );
}
